<template>
  <div class="field-creator">
    <a-form @submit.prevent="createField">
      <!-- Ortak Inputlar -->
      <a-form-item :label="$t('message.name')" :required="true">
        <a-input v-model="field.name" />
      </a-form-item>

      <a-form-item :label="$t('tables.fieldType')" :required="true">
        <a-select v-model="field.fieldType"  @change="onFieldTypeChange">
          <a-select-option v-for="type in fieldTypes" :key="type.value" :value="type.value">
            {{ type.label }}
          </a-select-option>
        </a-select>
      </a-form-item>

      <a-form-item :label="$t('tables.alignment')" :required="true">
        <a-select v-model="field.alignment">
          <a-select-option value="left">{{ $t("tables.left") }}</a-select-option>
          <a-select-option value="center">{{ $t("tables.center") }}</a-select-option>
          <a-select-option value="right">{{ $t("tables.right") }}</a-select-option>
        </a-select>
      </a-form-item>

      <!-- Özel Inputlar -->
      <template v-if="field.fieldType === 'datetime'">
        <a-divider>Datetime Options</a-divider>
        <a-form-item :label="$t('tables.timezone')">
          <a-auto-complete
            v-model="field.options.datetime.timezone"
            :dataSource="timezones"
            :placeholder="$t('tables.timezone')"
            :filterOption="filterOption"
          />
        </a-form-item>
        <a-form-item label="Format">
          <a-select v-model="field.options.datetime.format">
            <a-select-option value="MM/DD/YYYY">MM/DD/YYYY (12/31/2023)</a-select-option>
            <a-select-option value="DD/MM/YYYY">DD/MM/YYYY (31/12/2023)</a-select-option>
            <a-select-option value="YYYY-MM-DD">YYYY-MM-DD (2023-12-31)</a-select-option>
            <a-select-option value="MM-DD-YYYY">MM-DD-YYYY (12-31-2023)</a-select-option>
            <a-select-option value="DD-MM-YYYY">DD-MM-YYYY (31-12-2023)</a-select-option>
            <a-select-option value="YYYY/MM/DD">YYYY/MM/DD (2023/12/31)</a-select-option>
            <a-select-option value="MMM DD, YYYY">MMM DD, YYYY (Dec 31, 2023)</a-select-option>
            <a-select-option value="DD MMM, YYYY">DD MMM, YYYY (31 Dec, 2023)</a-select-option>
            <a-select-option value="MMMM DD, YYYY">MMMM DD, YYYY (December 31, 2023)</a-select-option>
            <a-select-option value="DD MMMM, YYYY">DD MMMM, YYYY (31 December, 2023)</a-select-option>
          </a-select>
        </a-form-item>
        <a-form-item :label="$t('tables.includeTime')">
          <a-switch v-model="field.options.datetime.includeTime" />
        </a-form-item>
        <a-form-item v-if="field.options.datetime.includeTime" :label="$t('tables.timeFormat')">
          <a-select v-model="field.options.datetime.timeFormat">
            <a-select-option value="HH:mm">HH:mm (23:59)</a-select-option>
            <a-select-option value="hh:mm A">hh:mm A (11:59 PM)</a-select-option>
            <a-select-option value="HH:mm:ss">HH:mm:ss (23:59:59)</a-select-option>
            <a-select-option value="hh:mm:ss A">hh:mm:ss A (11:59:59 PM)</a-select-option>
            <a-select-option value="HH:mm:ss.SSS">HH:mm:ss.SSS (23:59:59.999)</a-select-option>
            <a-select-option value="hh:mm:ss.SSS A">hh:mm:ss.SSS A (11:59:59.999 PM)</a-select-option>
          </a-select>
        </a-form-item>
      </template>

      <template v-else-if="field.fieldType === 'number'">
        <a-divider>Number Options</a-divider>
        <a-form-item :label="$t('tables.decimalFormat')">
          <a-select v-model="field.options.number.decimalFormat">
            <a-select-option value="0">0</a-select-option>
            <a-select-option value="0.0">0.0</a-select-option>
            <a-select-option value="0.00">0.00</a-select-option>
            <a-select-option value="0.000">0.000</a-select-option>
            <a-select-option value="0.0000">0.0000</a-select-option>
            <a-select-option value="0.00000">0.00000</a-select-option>
            <a-select-option value="0.000000">0.000000</a-select-option>
          </a-select>
        </a-form-item>
      </template>

      <template v-else-if="field.fieldType === 'currency'">
        <a-divider>Currency Options</a-divider>
        <a-form-item :label="$t('tables.currencySymbol')">
          <a-select v-model="field.options.currency.symbol">
            <a-select-option value="$">$ (USD)</a-select-option>
            <a-select-option value="€">€ (EUR)</a-select-option>
            <a-select-option value="£">£ (GBP)</a-select-option>
            <a-select-option value="¥">¥ (JPY)</a-select-option>
            <a-select-option value="₽">₽ (RUB)</a-select-option>
            <a-select-option value="₹">₹ (INR)</a-select-option>
            <a-select-option value="₺">₺ (TRY)</a-select-option>
            <a-select-option value="₩">₩ (KRW)</a-select-option>
            <a-select-option value="R$">R$ (BRL)</a-select-option>
            <a-select-option value="¥">¥ (CNY)</a-select-option>
          </a-select>
        </a-form-item>
        <a-form-item :label="$t('tables.decimalFormat')">
          <a-select v-model="field.options.currency.decimalFormat">
            <a-select-option value="0">0</a-select-option>
            <a-select-option value="0.0">0.0</a-select-option>
            <a-select-option value="0.00">0.00</a-select-option>
            <a-select-option value="0.000">0.000</a-select-option>
            <a-select-option value="0.0000">0.0000</a-select-option>
            <a-select-option value="0.00000">0.00000</a-select-option>
            <a-select-option value="0.000000">0.000000</a-select-option>
          </a-select>
        </a-form-item>
      </template>

      <template v-else-if="field.fieldType === 'dropdown'">
        <a-divider>Dropdown Options</a-divider>
        <a-form-item :label="$t('tables.mode')">
          <a-select v-model="field.options.dropdown.mode">
            <a-select-option value="static">{{ $t('tables.statics') }}</a-select-option>
            <a-select-option value="dynamic">{{ $t('tables.dynamics') }}</a-select-option>
          </a-select>
        </a-form-item>
        <a-form-item label="Multiple Selection">
          <a-switch v-model="field.options.dropdown.multiple" />
        </a-form-item>

        <!-- Static Mode Options -->
        <div v-if="field.options.dropdown.mode === 'static'">
          <a-form-item label="Options">
            <div v-for="(option, index) in field.options.dropdown.options" :key="index" class="option-item">
              <a-input v-model="option.value" placeholder="Value" style="width: 60%;" />
              <a-select v-model="option.color" placeholder="Color" style="width: 30%; margin-left: 10px;">
                <template #suffixIcon>
                  <div class="color-box" :style="{ backgroundColor: selectedColor(option.color) }"></div>
                </template>
                <a-select-option v-for="(color, colorIndex) in colors" :key="colorIndex" :value="color.value">
                  <div class="color-option">
                    <div class="color-box" :style="{ backgroundColor: color.value }"></div>
                    <span>{{ color.label }}</span>
                  </div>
                </a-select-option>
              </a-select>
              <a-button type="danger" icon="delete" @click="removeOption(index)" style="margin-left: 10px;" />
            </div>
            <a-button type="dashed" icon="plus" @click="addOption">{{ $t("tables.addOption") }}</a-button>
          </a-form-item>
        </div>
      </template>

      <template v-else-if="field.fieldType === 'automation-button'">
        <a-divider>Automation Button Options</a-divider>
        <a-form-item label="Automation Name">
          <a-input v-model="field.options.button.automationName" />
        </a-form-item>
        <a-form-item label="Default Label" :required="true">
          <a-input v-model="field.options.button.defaultLabel" />
        </a-form-item>
        <a-form-item label="Disable When Clicked">
          <a-switch v-model="field.options.button.disableWhenClicked" />
        </a-form-item>
      </template>

      <!-- Form Butonları -->
      <a-form-item>
        <a-button type="primary" html-type="submit" :loading="loading" :disabled="loading">{{ $t("tables.create") }}</a-button>
        <a-button @click="cancel" :disabled="loading">{{ $t("message.cancel") }}</a-button>
      </a-form-item>
    </a-form>
  </div>
</template>

<script>
import Vue from "vue";
import { TABLE_API } from "@/api/table"; // API'yi import edin
import moment from "moment-timezone";

export default Vue.extend({
  name: "FieldCreator",
  props: {
    defaultFieldType: {
      type: String,
      default: null,
    },
    tableId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      loading: false, // Loading durumunu yönetecek değişken
      field: {
        name: "",
        fieldType: this.defaultFieldType || "text",
        alignment: "left",
        options: {
          datetime: {
            timezone: "UTC",
            format: "YYYY-MM-DD",
            includeTime: true,
            timeFormat: "HH:mm",
          },
          number: {
            decimalFormat: "0", // Varsayılan değer
          },
          currency: {
            symbol: "$",
            decimalFormat: "00",
          },
          dropdown: {
            mode: "static",
            multiple: false,
            options: [
              { value: "", color: "rgb(210, 216, 243)" },
            ],
            remoteTable: null,
            remoteTableField: null,
          },
          button: {
            automationName: "",
            defaultLabel: "",
            disableWhenClicked: false,
          },
        },
      },
      fieldTypes: [
        { value: "text", label: this.$t("tables.text") },
        { value: "long-text", label: this.$t("tables.longText") },
        { value: "number", label: this.$t("tables.number")},
        { value: "currency", label: this.$t("tables.currency") },
        { value: "datetime", label: this.$t("tables.datetime") },
        { value: "dropdown", label: this.$t("tables.dropdown") },
        { value: "checkbox", label: this.$t("tables.checkbox") },
        { value: "url", label: "URL" },
        { value: "email", label: this.$t("tables.email") },
        { value: "phone", label: this.$t("tables.phone") },
        { value: "json", label: "JSON" },
        { value: "file", label: this.$t("tables.file") },
        { value: "image", label: this.$t("tables.image") },
        { value: "video", label: this.$t("tables.video") },
        { value: "audio", label: this.$t("tables.audio") },
        { value: "color", label: this.$t("tables.color") },
        { value: "automation-button", label: this.$t("tables.automationButton") },
      ],
      colors: [
        { label: "Gray", value: "rgb(232, 231, 228)" },
        { label: "Blue", value: "rgb(210, 216, 243)" },
        { label: "Green", value: "rgb(222, 243, 233)" },
        { label: "Yellow", value: "rgb(255, 244, 188)" },
        { label: "Red", value: "rgb(250, 223, 217)" },
        { label: "Pink", value: "rgb(250, 217, 239)" },
        { label: "Purple", value: "rgb(235, 217, 250)" },
        { label: "Cream", value: "rgb(255, 243, 230)" },
      ],
      timezones: moment.tz.names(),
    };
  },
  methods: {
    onFieldTypeChange() {
      // Özel alanları sıfırlayabilir veya işlemler yapabilirsiniz
    },
    addOption() {
      const nextColor = this.colors[this.field.options.dropdown.options.length % this.colors.length];
      this.field.options.dropdown.options.push({ value: "", color: nextColor.value });
    },
    removeOption(index) {
      this.field.options.dropdown.options.splice(index, 1);
    },
    async createField() {
      this.loading = true; // İşlem başlıyor
      try {
        // API'ye istek gönder
        const response = await TABLE_API.createField(this.tableId, this.field);
        // Başarılı mesajını göster
        this.$message.success(this.$t("tables.createFieldSucs"));
        // Üst componente tabloyu yenilemesini bildir
        this.$emit("field-created-success");
        // Modal'ı kapat
        this.cancel();
      } catch (error) {
        console.error(this.$t("tables.failedCreateField"), error);
        // Hata mesajını göster
        this.$message.error(this.$t("tables.createFieldError"));
      }
      this.loading = false; // İşlem tamamlandı
    },
    cancel() {
      // İşlemi iptal et
      this.$emit("cancel");
    },
    selectedColor(colorValue) {
      const selectedColor = this.colors.find((color) => color.value === colorValue);
      return selectedColor ? selectedColor.value : "";
    },
    filterOption(input, option) {
      return option.componentOptions.children[0].text.toLowerCase().indexOf(input.toLowerCase()) >= 0;
    },
  },
});
</script>

<style scoped lang="scss">
.field-creator {
  .option-item {
    display: flex;
    align-items: center;
    margin-bottom: 10px;
  }
}
.ant-form-item{
    margin-bottom: 5px;
}
.color-box {
  width: 16px;
  height: 16px;
  border: 1px solid #000;
  display: inline-block;
  vertical-align: middle;
  margin-right: 5px;
}

.color-option {
  display: flex;
  align-items: center;
}
</style>
